type Primitive = string | number | boolean;
export type FilterState = Record<string, Primitive | Primitive[] | Record<string, Primitive> | undefined | null>;

/**
 * Compares two filter states excluding specified keys
 * @param filters1 First filter state
 * @param filters2 Second filter state
 * @param excludeKeys Keys to exclude from comparison (e.g., ['q'] for search query)
 * @returns true if filters are equal (excluding specified keys)
 */
export const compareFilters = <T extends FilterState>(
  filters1: T,
  filters2: T,
  excludeKeys: (keyof T)[] = []
): boolean => {
  const filter1Copy = { ...filters1 };
  const filter2Copy = { ...filters2 };

  // Remove excluded keys from both objects
  excludeKeys.forEach((key) => {
    delete filter1Copy[key];
    delete filter2Copy[key];
  });

  return JSON.stringify(filter1Copy) === JSON.stringify(filter2Copy);
};

/**
 * Checks if filters match default values excluding specified keys
 * @param filters Current filter state
 * @param defaultFilters Default filter state
 * @param excludeKeys Keys to exclude from comparison (e.g., ['q'] for search query)
 * @returns true if current filters match defaults (excluding specified keys)
 */
export const isDefaultFilters = <T extends FilterState>(
  filters: T,
  defaultFilters: T,
  excludeKeys: (keyof T)[] = []
): boolean => {
  return compareFilters(filters, defaultFilters, excludeKeys);
};
